import { mapValues } from 'es-toolkit';
import React, { useState } from 'react';
import { Alert, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { positiveQuantityOrNull, toUploadFile, useHiddenFileSelect } from '@paragon/client-tools';
import { ComponentDesign } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getClientUrls, getLoggedInUserOrganization } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';

import { componentDesignsSlice } from '../../../store/componentDesignsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { acceptedFileTypesForComponentUpload } from '../../pages/acceptedFileTypesForComponentUpload';

interface CreateAssemblyGroupsChooseSourceProps {
  readonly initialLookupCode?: string;
  readonly componentDesigns: readonly ComponentDesign[] | null;
  readonly setComponentDesignsAndInfo: (
    componentDesigns: readonly ComponentDesign[] | null,
    componentDesignGuidToQuantity: Record<string, number>,
    inferredGroupName: string | null,
  ) => void;
  readonly setIsWorking: (isWorking: boolean) => void;
  readonly setFinishedChoosingSource: () => void;
}

export function CreateAssemblyGroupsChooseSource({
  initialLookupCode,
  componentDesigns,
  setComponentDesignsAndInfo,
  setIsWorking,
  setFinishedChoosingSource,
}: CreateAssemblyGroupsChooseSourceProps) {
  const dispatch = useAppDispatch();
  const loggedInUserOrganization = useAppSelector(getLoggedInUserOrganization);
  const designUrlForEnvironment = useAppSelector(getClientUrls).designUrl;

  const [inputMethod, setInputMethod] = useState<'select-files' | 'enter-lookup-code'>(
    initialLookupCode ? 'enter-lookup-code' : 'select-files',
  );
  const [sharedComponentSetLookupCode, setSharedComponentSetLookupCode] = useState<string>(initialLookupCode ?? '');
  const [sharedComponentSetLookupCodeError, setSharedComponentSetLookupCodeError] = useState<string | null>(null);
  const [filesErrorsCount, setFilesErrorsCount] = useState<number | null>(null);
  const [renderHiddenSelect, selectFile] = useHiddenFileSelect(acceptedFileTypesForComponentUpload, true);

  const filesProblem =
    inputMethod === 'select-files' && componentDesigns?.length === 0 ? 'No component designs found in the files' : null;

  async function onClickSelectFiles() {
    if (!loggedInUserOrganization) throw new Error('No logged in user');

    const selectedFiles = await selectFile();
    setIsWorking(true);
    try {
      const uploadFilesResult = await designService.componentDesigns.postUploadFiles(
        selectedFiles.map(toUploadFile),
        loggedInUserOrganization,
        null,
      );
      const componentDesigns = uploadFilesResult.componentDesignsWithTimestamps.map(
        ({ componentDesign }) => componentDesign,
      );
      setComponentDesignsAndInfo(
        componentDesigns,
        mapValues(uploadFilesResult.quantitiesFromFiles, (quantity) => positiveQuantityOrNull(quantity) ?? 1),
        uploadFilesResult.inferredGroupName,
      );
      dispatch(componentDesignsSlice.actions.loadedFromServer(componentDesigns));
      setFilesErrorsCount(uploadFilesResult.errors.length);
      if (uploadFilesResult.errors.length === 0 && componentDesigns.length > 0) {
        setFinishedChoosingSource();
      }
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError('Error reading files. Please try again and contact help@paragontruss.com if the issue persists.'),
      );
    } finally {
      setIsWorking(false);
    }
  }

  async function onClickInputFromLookupCode() {
    if (sharedComponentSetLookupCode.trim().length !== 10) {
      setSharedComponentSetLookupCodeError('Lookup code must be 10 characters');
      return;
    }

    try {
      setIsWorking(true);
      const insertComponentDesignsFromLookupCodeResponse =
        await designService.sharedComponentSets.insertComponentDesignsFromLookupCode(
          sharedComponentSetLookupCode.trim(),
        );
      const { componentDesignsWithUpdatedGuids, newComponentDesignGuidToQuantity, sharedComponentSetName } =
        insertComponentDesignsFromLookupCodeResponse;
      setSharedComponentSetLookupCodeError(null);
      setComponentDesignsAndInfo(
        componentDesignsWithUpdatedGuids,
        newComponentDesignGuidToQuantity,
        sharedComponentSetName,
      );
      dispatch(componentDesignsSlice.actions.loadedFromServer(componentDesignsWithUpdatedGuids));
      if (componentDesignsWithUpdatedGuids.length > 0) {
        setFinishedChoosingSource();
      }
    } catch (error) {
      console.error(error);
      setSharedComponentSetLookupCodeError('That lookup code could not be found');
    } finally {
      setIsWorking(false);
    }
  }

  return (
    <>
      {renderHiddenSelect()}

      <ToggleButtonGroup
        exclusive
        value={inputMethod}
        onChange={(_, value) => {
          setComponentDesignsAndInfo(null, {}, null);
          setFilesErrorsCount(null);
          setInputMethod(value);
        }}
      >
        <ToggleButton value="select-files">Select Files</ToggleButton>
        <ToggleButton value="enter-lookup-code">Enter Lookup Code</ToggleButton>
      </ToggleButtonGroup>

      {inputMethod === 'select-files' && (
        <>
          <Button variant="outlined" sx={{ width: '100%' }} onClick={onClickSelectFiles}>
            Select Files
          </Button>

          {filesProblem && (
            <Alert variant="outlined" severity="error">
              {filesProblem}
            </Alert>
          )}
        </>
      )}

      {inputMethod === 'enter-lookup-code' && (
        <>
          <Stack direction="row" spacing={1}>
            <TextField
              autoFocus
              autoComplete="off"
              size="small"
              label="Shared Component Set Lookup Code"
              sx={{ marginTop: 1, width: '100%' }}
              value={sharedComponentSetLookupCode}
              error={!!sharedComponentSetLookupCodeError}
              onFocus={(event) => event.target.select()}
              onChange={(event) => {
                let lookupCode = event.target.value.toUpperCase();
                if (lookupCode.startsWith(designUrlForEnvironment.toUpperCase())) {
                  lookupCode = lookupCode
                    .slice(designUrlForEnvironment.length, designUrlForEnvironment.length + 10)
                    .toUpperCase();
                }
                setSharedComponentSetLookupCode(lookupCode);
              }}
            />
            <Button sx={{ width: '150px' }} variant="outlined" onClick={onClickInputFromLookupCode}>
              Look Up
            </Button>
          </Stack>

          {sharedComponentSetLookupCodeError && (
            <Alert variant="outlined" severity="error">
              {sharedComponentSetLookupCodeError}
            </Alert>
          )}
        </>
      )}

      {(componentDesigns?.length ?? 0) > 0 && (
        <Typography>
          {`${
            componentDesigns?.length ?? 0
          } component design${componentDesigns?.length === 1 ? '' : 's'} found${(filesErrorsCount ?? 0 > 0) ? `, ${filesErrorsCount} failed` : ''}`}
        </Typography>
      )}

      {filesErrorsCount !== null && filesErrorsCount > 0 && (
        <Button variant="outlined" onClick={() => void setFinishedChoosingSource()}>
          Continue with {componentDesigns?.length ?? 0} component design
          {(componentDesigns?.length ?? 0) === 1 ? '' : 's'}
        </Button>
      )}
    </>
  );
}
